import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Add as AddIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUp as VolumeUpIcon,
  InsertDriveFile as FileIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { MessageBubble } from '@/components/chat/MessageBubble'
import { ChatDateDivider } from '@/components/chat/ChatDateDivider'
import { localDayKey } from '@/components/chat/chatDay'
import { EmojiStickerPanel } from '@/components/chat/EmojiStickerPanel'
import { useChatStore } from '@/stores/chatStore'
import { useAuthStore } from '@/stores/authStore'
import { chatApi } from '@/api/chat'
import type { ChatAttachment, ChatContact, ChatMessage, ChatRoom } from '@/api/chat'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { extractDataTransferFiles } from '@/utils/clipboard'
import { unlockChatSound } from '@/utils/chatSound'
import { storagePublicUrl } from '@/utils/storage'
import '@/styles/dashboard.css'

const MUTE_UNTIL_FAR_FUTURE = '2099-12-31T23:59:59.000Z'
const IMAGE_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024
const FILE_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
/** Зеркало правила `attachments => array|max:10` backend (SendMessageRequest). */
const MAX_PENDING_ATTACHMENTS = 10
/**
 * Белый список типов вложений — зеркало `config/chat.php` на бэкенде.
 *
 * Расширения, а не mime: браузеры проставляют File.type неровно (у .docx и
 * .zip он часто пуст или application/octet-stream), а сервер валидирует
 * именно по расширению.
 */
const ACCEPTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'] as const
const ACCEPTED_DOCUMENT_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip',
] as const
const ACCEPTED_EXTENSIONS: readonly string[] = [
  ...ACCEPTED_IMAGE_EXTENSIONS,
  ...ACCEPTED_DOCUMENT_EXTENSIONS,
]

/** Значение атрибута accept скрытого input — тот же список. */
const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  ...ACCEPTED_DOCUMENT_EXTENSIONS.map((extension) => `.${extension}`),
].join(',')

const fileExtension = (file: File): string => {
  const dot = file.name.lastIndexOf('.')

  return dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase()
}

/**
 * Клиент отбивает только то, в чём уверен.
 *
 * Сервер валидирует правилом `mimes:`, то есть по угаданному из содержимого
 * типу, а не по имени файла. Поэтому файл без расширения (перетаскивание из
 * вкладки браузера, файлы без суффикса) пропускаем к серверу — иначе клиент
 * запрещал бы то, что сервер принимает. Заявленное чужое расширение — .exe,
 * .iso, .mp4 — отбиваем здесь: ради этого проверка и добавлена.
 */
const isAcceptedFile = (file: File): boolean => {
  const extension = fileExtension(file)

  if (ACCEPTED_EXTENSIONS.includes(extension)) return true

  return extension === ''
}

/** Высота верхней строки панели: «Новый чат» слева = заголовок чата справа. */
const CHAT_TOOLBAR_ROW_SX = {
  px: 1.5,
  py: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
} as const

export function ChatPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ userId?: string; groupId?: string; roomId?: string }>()
  const currentUser = useAuthStore((s) => s.user)
  const currentUserId = currentUser?.id ?? 0

  const {
    rooms,
    contacts,
    currentRoomId,
    byRoom,
    isLoadingRooms,
    isLoadingMessages,
    isLoadingContacts,
    error,
    fetchRooms,
    fetchContacts,
    openDirect,
    setCurrentRoom,
    fetchMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    markRead,
    setRoomMuted,
    toggleReaction,
    clearError,
  } = useChatStore()

  const [deepLinkError, setDeepLinkError] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([])
  const [uploadingRoomIds, setUploadingRoomIds] = useState<number[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [newChatOpen, setNewChatOpen] = useState(false)
  const [isMuting, setIsMuting] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [roomSearchQuery, setRoomSearchQuery] = useState('')
  const [attachMenuAnchor, setAttachMenuAnchor] = useState<HTMLElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const textInputRef = useRef<HTMLTextAreaElement | null>(null)
  const listEndRef = useRef<HTMLDivElement | null>(null)
  const dragDepthRef = useRef(0)
  /**
   * Комнаты, в которые прямо сейчас идёт загрузка вложений. Ref ставится
   * синхронно — state обновится только к следующему рендеру, а вторую пачку в
   * ту же комнату надо отсечь сразу. Учёт покомнатный: загрузка в одном чате
   * не должна мешать прикреплять файлы и писать в других.
   */
  const uploadingRoomIdsRef = useRef<Set<number>>(new Set())
  const setUploadingRoom = (roomId: number, uploading: boolean) => {
    if (uploading) uploadingRoomIdsRef.current.add(roomId)
    else uploadingRoomIdsRef.current.delete(roomId)
    setUploadingRoomIds([...uploadingRoomIdsRef.current])
  }
  /**
   * Поколение композера. Смена комнаты, переход в режим правки и удачная
   * отправка обнуляют отложенные вложения — догрузившийся после этого файл не
   * должен приземлиться в уже очищенный композер (тем более чужой комнаты).
   */
  const composerGenerationRef = useRef(0)
  const resetComposerGeneration = () => {
    composerGenerationRef.current += 1
  }

  useEffect(() => {
    void fetchRooms()
  }, [fetchRooms])

  useEffect(() => {
    if (!params.roomId) return
    const roomId = Number(params.roomId)
    if (!Number.isFinite(roomId)) return
    setCurrentRoom(roomId)
    navigate('/chat', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.roomId])

  useEffect(() => {
    if (!params.userId) return
    const userId = Number(params.userId)
    if (!Number.isFinite(userId)) return
    openDirect(userId)
      .then(() => navigate('/chat', { replace: true }))
      .catch((e) => setDeepLinkError(getErrorMessage(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.userId])

  useEffect(() => {
    if (!params.groupId) return
    const groupId = Number(params.groupId)
    if (!Number.isFinite(groupId)) return
    const room = rooms.find((r) => r.student_group_id === groupId)
    if (room) {
      setCurrentRoom(room.id)
      navigate('/chat', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.groupId, rooms])

  const currentRoom = useMemo(
    () => rooms.find((r) => r.id === currentRoomId) ?? null,
    [rooms, currentRoomId]
  )
  const roomState = currentRoomId ? byRoom[currentRoomId] : undefined
  // Через useMemo: `?? []` создавал новый массив на каждый рендер, и
  // группировка ниже пересчитывалась даже когда лента не менялась.
  const messages = useMemo(() => roomState?.messages ?? [], [roomState?.messages])
  const canWrite = currentRoom?.can_write ?? false

  // Группы считаются в рендере, а не при загрузке: так они сами
  // пересчитываются и после «Загрузить ещё» (старая страница подклеивается
  // в начало), и после realtime-сообщения в конце.
  //
  // Результат — именно группы, а не помеченные сообщения: липкая плашка
  // ограничена своим блоком, и обёртка вокруг одного пузыря держала бы её
  // ровно на его высоту.
  const dayGroups = useMemo(() => {
    const groups: Array<{ key: string; dividerIso: string | null; messages: typeof messages }> = []
    let currentDay: string | null = null

    for (const message of messages) {
      const day = localDayKey(message.created_at)

      // created_at может быть null — такое сообщение остаётся в текущей
      // группе и день не переключает.
      if (day !== null && day !== currentDay) {
        currentDay = day
        groups.push({ key: `day-${day}-${message.id}`, dividerIso: message.created_at, messages: [message] })

        continue
      }

      if (groups.length === 0) {
        groups.push({ key: `day-none-${message.id}`, dividerIso: null, messages: [message] })

        continue
      }

      groups[groups.length - 1].messages.push(message)
    }

    return groups
  }, [messages])
  /** Загрузка идёт именно в этот чат — только она гасит здешние кнопки. */
  const isUploadingHere = currentRoomId !== null && uploadingRoomIds.includes(currentRoomId)

  const filteredRooms = useMemo(
    () => filterRoomsBySearch(rooms, roomSearchQuery),
    [rooms, roomSearchQuery]
  )

  useEffect(() => {
    if (currentRoomId && !byRoom[currentRoomId]?.loaded) {
      void fetchMessages(currentRoomId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoomId])

  useEffect(() => {
    if (!currentRoomId) return
    const roomId = currentRoomId
    const heartbeat = () => {
      void chatApi.enterPresence(roomId).catch(() => {})
    }
    heartbeat()
    const intervalId = window.setInterval(heartbeat, 30_000)
    return () => {
      window.clearInterval(intervalId)
      void chatApi.leavePresence(roomId).catch(() => {})
    }
  }, [currentRoomId])

  useEffect(() => {
    dragDepthRef.current = 0
    setIsDragOver(false)
  }, [currentRoomId, canWrite])

  useEffect(() => {
    if (!currentRoomId || messages.length === 0) return
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    const lastId = messages[messages.length - 1]?.id
    if (lastId) void markRead(currentRoomId, lastId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, currentRoomId])

  /**
   * Композер принадлежит комнате. Комнату меняют не только клики по списку, но
   * и «Новый чат», и deep-link'и `/chat/direct/:userId`, `/chat/group/:groupId`,
   * `/chat/:roomId` — поэтому очистка висит на самом `currentRoomId`, а не на
   * обработчике клика. Заодно растёт поколение композера: догружающаяся пачка
   * вложений не должна приземлиться в чат, который пользователь уже сменил.
   */
  useEffect(() => {
    resetComposerGeneration()
    setEditingId(null)
    setText('')
    setPendingAttachments([])
  }, [currentRoomId])

  const handleSelectRoom = (roomId: number) => {
    setCurrentRoom(roomId)
  }

  const handleOpenNewChat = async () => {
    unlockChatSound()
    setNewChatOpen(true)
    if (contacts.length === 0) {
      await fetchContacts()
    }
  }

  const handleSelectContact = async (contact: ChatContact) => {
    setNewChatOpen(false)
    setDeepLinkError(null)
    try {
      if (contact.existing_room_id) {
        setCurrentRoom(contact.existing_room_id)
      } else {
        await openDirect(contact.user.id)
      }
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  const handleToggleMute = async () => {
    if (!currentRoomId || !currentRoom) return
    setIsMuting(true)
    try {
      const nextMuted = !currentRoom.is_muted
      await chatApi.mute(currentRoomId, nextMuted ? MUTE_UNTIL_FAR_FUTURE : null)
      setRoomMuted(currentRoomId, nextMuted)
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    } finally {
      setIsMuting(false)
    }
  }

  /**
   * Единственный путь загрузки вложений — меню «Файл», drag-and-drop и вставка
   * из буфера.
   *
   * В одной комнате пачка одна: `uploadingRoomIdsRef` ставится синхронно, поэтому
   * вставка поверх незавершённого дропа не запускает второй проход. Без этого
   * два параллельных вызова считали бы свободные слоты каждый по своему
   * рендеру и вместе перевалили бы за лимит. Пачки в разных комнатах при этом
   * идут независимо: композер очищается на смене комнаты, а остаток «чужой»
   * пачки отсекает поколение.
   *
   * Свободные слоты внутри пачки считаются локально: setPendingAttachments не
   * обновляет замыкание, а лишние файлы нужно отсечь до отправки на сервер —
   * иначе send упрётся в лимит уже с мусором в хранилище.
   */
  const uploadFiles = async (files: File[]) => {
    if (!currentRoomId || !canWrite || files.length === 0) return
    const roomId = currentRoomId
    if (uploadingRoomIdsRef.current.has(roomId)) {
      setDeepLinkError(t('chat.uploadInProgress'))
      return
    }

    let freeSlots = MAX_PENDING_ATTACHMENTS - pendingAttachments.length
    if (freeSlots <= 0) {
      setDeepLinkError(t('chat.attachmentsLimit', { max: MAX_PENDING_ATTACHMENTS }))
      return
    }

    unlockChatSound()
    setDeepLinkError(null)
    const generation = composerGenerationRef.current
    setUploadingRoom(roomId, true)
    try {
      for (const file of files) {
        if (freeSlots === 0) {
          setDeepLinkError(t('chat.attachmentsLimit', { max: MAX_PENDING_ATTACHMENTS }))
          break
        }
        // Тип проверяем до отправки: accept висит только на скрытом input, а
        // перетаскивание и вставка из буфера идут сюда же. Без проверки .exe
        // или .iso целиком уезжали на сервер и возвращались 422 без внятного
        // объяснения, какой файл не подошёл.
        if (!isAcceptedFile(file)) {
          setDeepLinkError(t('chat.fileTypeNotSupported', { name: file.name }))
          continue
        }

        const maxSize = file.type.startsWith('image/')
          ? IMAGE_MAX_FILE_SIZE_BYTES
          : FILE_MAX_FILE_SIZE_BYTES
        if (file.size > maxSize) {
          setDeepLinkError(
            file.type.startsWith('image/')
              ? t('chat.imageTooLarge')
              : t('chat.fileTooLarge')
          )
          continue
        }
        // Ошибка одного файла не должна ронять остальную пачку: показываем её
        // и продолжаем, иначе после 422 на третьем файле молча пропадали бы
        // все следующие.
        try {
          const attachment = await chatApi.uploadFile(roomId, file)
          // Пока файл летел на сервер, композер могли очистить: уйти в другую
          // комнату, начать правку сообщения или отправить его. Дописывать в
          // него остаток пачки нельзя — вложения оказались бы в чужом чате или
          // в чужом сообщении.
          if (composerGenerationRef.current !== generation) break
          setPendingAttachments((prev) => [...prev, attachment])
          freeSlots -= 1
        } catch (e) {
          // Таймаут и обрыв сети дают технический текст axios — показываем
          // вместо него понятную строку, как и на остальных экранах.
          setDeepLinkError(
            displayErrorMessage(getErrorMessage(e), t('chat.uploadFailed'))
          )
        }
      }
    } finally {
      setUploadingRoom(roomId, false)
    }
  }

  const handleFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    event.target.value = ''
    if (!fileList?.length) return
    await uploadFiles(Array.from(fileList))
  }

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!canWrite || !currentRoomId) return
    if (!event.dataTransfer.types.includes('Files')) return
    dragDepthRef.current += 1
    setIsDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current -= 1
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0
      setIsDragOver(false)
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (canWrite && currentRoomId) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = 0
    setIsDragOver(false)
    if (!canWrite || !currentRoomId) return
    void uploadFiles(extractDataTransferFiles(event.dataTransfer))
  }

  const handleSend = async () => {
    if (!currentRoomId || !canWrite) return
    // Отправка посреди загрузки увела бы сообщение с половиной вложений, а
    // догрузившиеся файлы остались бы висеть в композере уже после отправки.
    // Блокируется только комната, куда идёт загрузка: в остальных чатах писать
    // можно как обычно.
    if (uploadingRoomIdsRef.current.has(currentRoomId)) {
      setDeepLinkError(t('chat.uploadInProgress'))
      return
    }
    const trimmed = text.trim()
    if (!trimmed && pendingAttachments.length === 0) return

    const payload = {
      text: trimmed || null,
      // pendingAttachments — только image/file (стикеры отправляются сразу из
      // picker); фильтр вместо каста — случайный сторонний тип не улетит
      // на сервер с полями image-вложения.
      attachments: pendingAttachments
        .filter((a): a is ChatAttachment & { type: 'image' | 'file' } =>
          a.type === 'image' || a.type === 'file'
        )
        .map((a) => ({
          type: a.type,
          path: a.path,
          name: a.name,
          mime: a.mime ?? 'application/octet-stream',
          size: a.size ?? 0,
        })),
    }

    try {
      if (editingId) {
        await editMessage(currentRoomId, editingId, payload)
        setEditingId(null)
      } else {
        await sendMessage(currentRoomId, payload)
      }
      resetComposerGeneration()
      setText('')
      setPendingAttachments([])
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  /**
   * Вставка emoji в позицию курсора (или в конец) с возвратом фокуса и
   * каретки. Каретка и текст читаются синхронно (state updater обязан быть
   * чистым — StrictMode вызывает его дважды), фокус — после коммита кадра.
   */
  const insertEmoji = useCallback((emoji: string) => {
    const input = textInputRef.current
    setText((prev) => {
      const start = input?.selectionStart ?? prev.length
      const end = input?.selectionEnd ?? prev.length
      return prev.slice(0, start) + emoji + prev.slice(end)
    })
    const caret = (input?.selectionStart ?? 0) + emoji.length
    requestAnimationFrame(() => {
      input?.focus()
      input?.setSelectionRange(caret, caret)
    })
  }, [])

  /**
   * Ctrl+V в композере: скриншот и «Копировать изображение» идут вложением тем
   * же путём, что и drag-and-drop.
   *
   * Форматированный фрагмент отдаём браузеру целиком. Excel, Word и Outlook
   * кладут в буфер сразу текст, HTML и PNG-рендер выделения: перехватив такую
   * вставку, мы вместо текста молча прикрепили бы картинку с данными, которые
   * пользователь показывать не собирался. Признак такого буфера — текст плюс
   * `text/html`; копия файла из проводника (текст = имя файла, HTML нет) и
   * скриншот (текста нет вовсе) остаются вложением, и `preventDefault` не даёт
   * имени файла упасть в поле ввода.
   */
  const handlePaste = (event: React.ClipboardEvent) => {
    if (!canWrite || !currentRoomId) return
    const data = event.clipboardData
    const files = extractDataTransferFiles(data)
    if (files.length === 0) return
    const isFormattedFragment =
      Boolean(data.getData('text/plain').trim()) && data.types.includes('text/html')
    if (isFormattedFragment) return
    event.preventDefault()
    void uploadFiles(files)
  }

  /**
   * Копирование текста сообщения из контекстного меню ленты. Стабильная
   * ссылка обязательна: MessageBubble обёрнут в memo, новая функция на каждый
   * рендер перерисовывала бы всю ленту на каждый realtime-патч реакций.
   * navigator.clipboard недоступен вне защищённого контекста — тогда подсказка
   * вместо тихого молчания.
   */
  const handleCopyMessageText = useCallback(
    (message: ChatMessage) => {
      const value = message.text ?? ''
      if (!value.trim()) return
      if (typeof navigator.clipboard?.writeText !== 'function') {
        setDeepLinkError(t('chat.copyFailed'))
        return
      }
      // Копируем текст как есть: отступы важны для кода и разметки, лента
      // рисует их через whiteSpace: 'pre-wrap'.
      navigator.clipboard
        .writeText(value)
        .catch(() => setDeepLinkError(t('chat.copyFailed')))
    },
    [t]
  )

  const handleSendSticker = async (stickerId: number) => {
    if (!currentRoomId || !canWrite) return
    unlockChatSound()
    setDeepLinkError(null)
    try {
      await sendMessage(currentRoomId, {
        attachments: [{ type: 'sticker', sticker_id: stickerId }],
      })
    } catch (e) {
      setDeepLinkError(getErrorMessage(e))
    }
  }

  // Стабильные колбэки — иначе memo на MessageBubble бесполезен и каждый
  // символ в композере перерисовывает всю ленту.
  const handleToggleReaction = useCallback(
    (messageId: number, emoji: string) => {
      if (!currentRoomId) return
      void toggleReaction(currentRoomId, messageId, emoji, currentUserId)
    },
    [currentRoomId, toggleReaction, currentUserId]
  )

  const startEdit = useCallback((message: ChatMessage) => {
    resetComposerGeneration()
    setEditingId(message.id)
    setText(message.text ?? '')
    setPendingAttachments([])
  }, [])

  const handleDelete = useCallback(
    (message: ChatMessage) => {
      if (!currentRoomId) return
      deleteMessage(currentRoomId, message.id).catch((e: unknown) => {
        setDeepLinkError(getErrorMessage(e))
      })
    },
    [currentRoomId, deleteMessage]
  )

  return (
    <DashboardLayout>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('chat.title')}
        </Typography>
      </Box>

      {(error || deepLinkError) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setDeepLinkError(null)
            clearError()
          }}
        >
          {deepLinkError || error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 340px) 1fr' },
          gap: 2,
          height: { md: 'calc(100vh - 140px)' },
          width: '100%',
        }}
      >
        <Paper variant="outlined" sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={CHAT_TOOLBAR_ROW_SX}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => void handleOpenNewChat()}
              sx={{ height: 40, textTransform: 'none' }}
            >
              {t('chat.newChat')}
            </Button>
          </Box>

          <Box sx={{ px: 1.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('chat.searchRooms')}
              value={roomSearchQuery}
              onChange={(e) => setRoomSearchQuery(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: roomSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRoomSearchQuery('')} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {isLoadingRooms ? (
            <Box sx={{ p: 2 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={56} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : rooms.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('chat.noRooms')}</Typography>
            </Box>
          ) : filteredRooms.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('chat.noSearchResults')}</Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
              {filteredRooms.map((room) => (
                <ListItemButton
                  key={room.id}
                  selected={room.id === currentRoomId}
                  onClick={() => handleSelectRoom(room.id)}
                >
                  <ListItemAvatar>
                    <Badge color="primary" badgeContent={room.unread_count} overlap="circular">
                      <Avatar src={roomListAvatarUrl(room, currentUserId) ?? undefined}>
                        {room.type === 'group' ? <GroupIcon /> : (room.title?.[0] ?? '?')}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={room.title ?? t('chat.untitled')}
                    secondary={renderPreview(room, currentUserId, t)}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Paper
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 400, position: 'relative' }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isDragOver && canWrite && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                border: 2,
                borderColor: 'primary.main',
                borderStyle: 'dashed',
                borderRadius: 1,
                pointerEvents: 'none',
              }}
            >
              <Typography color="primary" fontWeight={600}>
                {t('chat.dropToAttach')}
              </Typography>
            </Box>
          )}
          {!currentRoom ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('chat.selectRoom')}</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  ...CHAT_TOOLBAR_ROW_SX,
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography
                  fontWeight={600}
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setInfoOpen(true)}
                >
                  {currentRoom.title ?? t('chat.untitled')}
                </Typography>
                <Tooltip title={currentRoom.is_muted ? t('chat.muteOn') : t('chat.muteOff')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void handleToggleMute()}
                      disabled={isMuting}
                    >
                      {currentRoom.is_muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {roomState?.hasMore && (
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => currentRoomId && fetchMessages(currentRoomId, { loadMore: true })}
                    >
                      <Typography variant="caption">{t('chat.loadMore')}</Typography>
                    </IconButton>
                  </Box>
                )}
                {isLoadingMessages && messages.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton
                        key={i}
                        variant="rounded"
                        height={48}
                        width={i % 2 === 0 ? '70%' : '55%'}
                        sx={{ alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end' }}
                      />
                    ))}
                  </Box>
                ) : (
                  dayGroups.map((group) => (
                    // Блок на весь день: он и есть containing block липкой
                    // плашки. Обёртка вокруг одного сообщения держала бы её
                    // на высоту одного пузыря, а общий контейнер на всю ленту —
                    // слепил бы плашки всех дней в стопку наверху.
                    <Box key={group.key}>
                      {group.dividerIso !== null && <ChatDateDivider iso={group.dividerIso} />}
                      {group.messages.map((message) => (
                        <MessageBubble
                          key={message.id}
                          message={message}
                          isOwn={message.author_user_id === currentUserId}
                          showAuthor={currentRoom.type === 'group'}
                          canWrite={canWrite}
                          currentUserId={currentUserId}
                          onEdit={startEdit}
                          onDelete={handleDelete}
                          onCopyText={handleCopyMessageText}
                          onToggleReaction={handleToggleReaction}
                        />
                      ))}
                    </Box>
                  ))
                )}
                <div ref={listEndRef} />
              </Box>

              <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5 }}>
                {!canWrite && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('chat.readOnly')}
                  </Alert>
                )}

                {pendingAttachments.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {pendingAttachments.map((a) => (
                      <Box key={a.path} sx={{ position: 'relative' }}>
                        {a.type === 'image' ? (
                          <img
                            src={storagePublicUrl(a.path)}
                            alt={a.name}
                            style={{ height: 56, borderRadius: 6, objectFit: 'cover' }}
                          />
                        ) : (
                          <Chip icon={<FileIcon />} label={a.name} size="small" />
                        )}
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper' }}
                          onClick={() =>
                            setPendingAttachments((prev) => prev.filter((x) => x.path !== a.path))
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple
                    hidden
                    disabled={!canWrite}
                    onChange={handleFilePick}
                  />
                  <IconButton
                    aria-label={t('chat.attachFile')}
                    onClick={(event) => setAttachMenuAnchor(event.currentTarget)}
                    disabled={!canWrite || isUploadingHere}
                  >
                    {isUploadingHere ? <CircularProgress size={20} /> : <AddIcon />}
                  </IconButton>
                  <Menu
                    anchorEl={attachMenuAnchor}
                    open={Boolean(attachMenuAnchor)}
                    onClose={() => setAttachMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <MenuItem
                      onClick={() => {
                        setAttachMenuAnchor(null)
                        fileInputRef.current?.click()
                      }}
                    >
                      <ListItemIcon>
                        <AttachFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('chat.attachFile')}</ListItemText>
                    </MenuItem>
                  </Menu>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    inputRef={textInputRef}
                    placeholder={
                      canWrite ? t('chat.messagePlaceholder') : t('chat.readOnly')
                    }
                    value={text}
                    disabled={!canWrite}
                    onChange={(e) => setText(e.target.value)}
                    onPaste={handlePaste}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && canWrite) {
                        e.preventDefault()
                        void handleSend()
                      }
                    }}
                  />
                  <EmojiStickerPanel
                    disabled={!canWrite}
                    roomId={currentRoomId}
                    onPickEmoji={insertEmoji}
                    onSendSticker={(stickerId) => void handleSendSticker(stickerId)}
                  />
                  <IconButton
                    color="primary"
                    onClick={() => void handleSend()}
                    disabled={
                      !canWrite || isUploadingHere
                    }
                  >
                    <SendIcon />
                  </IconButton>
                </Box>

                {editingId && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('chat.editingHint')}{' '}
                      <Box
                        component="span"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => {
                          setEditingId(null)
                          setText('')
                        }}
                      >
                        {t('chat.cancel')}
                      </Box>
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {currentRoom?.type === 'group' ? t('chat.groupInfo') : t('chat.participantInfo')}
        </DialogTitle>
        <DialogContent dividers>
          {(currentRoom?.members ?? []).map((member) => (
            <Box key={member.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Avatar src={member.user?.avatar_url ?? undefined} sx={{ width: 40, height: 40 }}>
                {member.user?.full_name?.[0] ?? '?'}
              </Avatar>
              <Box>
                <Typography variant="body2">{member.user?.full_name ?? `#${member.user_id}`}</Typography>
                {member.role === 'admin' && (
                  <Typography variant="caption" color="text.secondary">
                    {t('chat.roleAdmin')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={newChatOpen} onClose={() => setNewChatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('chat.selectContact')}</DialogTitle>
        <DialogContent dividers>
          {isLoadingContacts ? (
            <List disablePadding>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="60%" />
                </Box>
              ))}
            </List>
          ) : contacts.length === 0 ? (
            <Typography color="text.secondary">{t('chat.noContacts')}</Typography>
          ) : (
            <List disablePadding>
              {contacts.map((contact) => (
                <ListItemButton
                  key={contact.user.id}
                  onClick={() => void handleSelectContact(contact)}
                >
                  <ListItemAvatar>
                    <Avatar src={contact.user.avatar_url ?? undefined}>
                      {contact.user.full_name?.[0] ?? '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={contact.user.full_name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}

function filterRoomsBySearch(rooms: ChatRoom[], query: string): ChatRoom[] {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return rooms

  return rooms.filter((room) => roomMatchesSearch(room, normalized))
}

function roomMatchesSearch(room: ChatRoom, normalizedQuery: string): boolean {
  const parts: string[] = []

  if (room.title) parts.push(room.title)
  if (room.group?.name) parts.push(room.group.name)

  for (const member of room.members ?? []) {
    if (member.user?.full_name) parts.push(member.user.full_name)
  }

  const lastMessage = room.last_message
  if (lastMessage && !lastMessage.is_deleted) {
    if (lastMessage.text) parts.push(lastMessage.text)
    for (const attachment of lastMessage.attachments) {
      if (attachment.name) parts.push(attachment.name)
    }
  }

  const haystack = parts.join(' ').toLocaleLowerCase()
  return haystack.includes(normalizedQuery)
}

function roomListAvatarUrl(room: ChatRoom, currentUserId: number): string | null {
  if (room.type === 'group') {
    return null
  }
  const other = room.members?.find((m) => m.user_id !== currentUserId)
  return other?.user?.avatar_url ?? null
}

/**
 * Превью последнего сообщения в списке комнат: «Вы: текст» для своих и
 * «Имя: текст» для чужих в группе — как в Telegram и VK. В личной переписке
 * сообщение собеседника идёт без префикса: его имя уже стоит заголовком.
 */
function renderPreview(
  room: ChatRoom,
  currentUserId: number,
  t: (key: string) => string
): React.ReactNode {
  const message = room.last_message
  if (!message) return t('chat.noMessages')

  const body = previewBody(message, t)
  const author = previewAuthorPrefix(room, message, currentUserId, t)
  if (!author) return body

  return (
    <>
      <Box component="span" sx={{ color: 'text.primary' }}>
        {author}:{' '}
      </Box>
      {body}
    </>
  )
}

function previewBody(message: ChatMessage, t: (key: string) => string): string {
  if (message.is_deleted) return t('chat.messageDeleted')
  if (message.text) return message.text
  const firstAttachment = message.attachments[0]
  if (firstAttachment?.type === 'sticker') return t('chat.stickerPreview')
  if (firstAttachment?.type === 'file') return firstAttachment.name || t('chat.filePreview')
  return t('chat.imagePreview')
}

/**
 * Имя автора для префикса превью. В last_message списка комнат author приходит
 * загруженным, но на всякий случай есть запасной путь через участников
 * комнаты. Показываем первое слово имени — длинное ФИО съело бы всю строку.
 */
function previewAuthorPrefix(
  room: ChatRoom,
  message: ChatMessage,
  currentUserId: number,
  t: (key: string) => string
): string | null {
  if (message.author_user_id === currentUserId) return t('chat.previewYou')
  if (room.type !== 'group') return null

  const fullName = (
    message.author?.full_name ??
    room.members?.find((m) => m.user_id === message.author_user_id)?.user?.full_name ??
    ''
  ).trim()
  if (!fullName) return null

  return fullName.split(/\s+/)[0] || fullName
}

